import { ExpandMoreRounded } from '@mui/icons-material';
import {
  ButtonBase, Chip, InputBase, ListItem, Menu, MenuItem, Select, TextField, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DateField, DateTimeField } from '@mui/x-date-pickers';
import styled from 'styled-components';

const RootContainer = styled.div`
  width: 100%;
`;

interface DisplayContainerProps {
  disabled?: boolean
}

const DisplayContainer = styled.div<DisplayContainerProps>`
  &:hover {
    background-color: ${({ theme, disabled }) => (disabled ? 'inherit' : theme.palette.backdrop.dark)};
  }
  padding: ${({ theme }) => theme.spacing(1, 2)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  min-height: 40px;
  width: calc(100%);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface DisplayProps {
  $multiLine?: boolean
  $placeholder?: boolean
}

const Display = styled(Typography)<DisplayProps>`
  white-space: ${({ $multiLine }) => ($multiLine ? 'break-spaces' : 'nowrap')};
`;

const DropdownIcon = styled(ExpandMoreRounded)`
  && {
    color: ${({ theme }) => theme.palette.primary.dark};
  }
`;

const StyledTextField = styled(InputBase)`
  && {
    & .MuiInputBase-input {
      background-color: ${({ theme }) => (theme.palette.backdrop.main)};
      border-radius: ${({ theme }) => theme.shape.borderRadius}px;
      padding: ${({ theme }) => theme.spacing(1, 2)};
      transition: ${({ theme }) => theme.transitions.create(['border-color', 'box-shadow'])};
      border: 1px solid;
      border-color: transparent;
      height: 22px;
      &:focus {
        box-shadow: ${({ theme }) => alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.1rem;
        border-color: ${({ theme }) => theme.palette.primary.main};
      }
    }
    flex: 1 1 auto;
  }
`;

const StyledAutocompleteField = styled(TextField)`
  && {
    background-color: ${({ theme }) => (theme.palette.backdrop.main)};
    height: 22px;
    width: 100%;
  }
`;

const EditFieldContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const FieldButton = styled(ButtonBase)`
  && {
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    margin-left: ${({ theme }) => theme.spacing(1)};
    border: 0;
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    justify-content: center;
    align-items: center;
    display: flex;
    background-color: ${({ theme }) => theme.palette.darkPrimary.main};
    color: ${({ theme }) => theme.palette.darkPrimary.contrastText};
    box-shadow: 0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12);
    &:hover {
        background-color: ${({ theme }) => theme.palette.darkPrimary.dark};
    }
  }
`;

const Dropdown = styled(Select)`
  && {
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    transition: ${({ theme }) => theme.transitions.create(['border-color', 'box-shadow'])};
    background-color: ${({ theme }) => theme.palette.backdrop.main};
    border: 1px solid;
    box-shadow: ${({ theme }) => alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.1rem;
    border-color: ${({ theme }) => theme.palette.primary.main};
    & .MuiOutlinedInput-notchedOutline {
      border: none;
    };
    & .MuiSelect-select {
      padding: ${({ theme }) => theme.spacing(1, 2)};
    }
    flex: 1 1 auto;
    height: 40px;
  }
`;

const ArrayMenu = styled(Menu)`
  && {
    flex: 1 1 auto;
    position: initial;
    & .MuiPaper-root {
      z-index: ${({ theme }) => theme.zIndex.modal + 1};
    }
  }
`;

const DropdownItem = styled(MenuItem)`
  && {
    display: flex;
    justify-content: center;
  }
`;

const ArrayDropdownItem = styled(MenuItem)`
  && {
    display: flex;
    justify-content: center;
    flex: 1 1 auto;
  }
`;

const StyledDateTimeField = styled(DateTimeField)`
  && {
    & .MuiInputBase-root {
      background-color: ${({ theme }) => (theme.palette.backdrop.main)};
      border-radius: ${({ theme }) => theme.shape.borderRadius}px;
      padding: ${({ theme }) => theme.spacing(1, 2)};
      transition: ${({ theme }) => theme.transitions.create(['border-color', 'box-shadow'])};
      border: 1px solid;
      border-color: transparent;
      height: 40px;
      box-shadow: ${({ theme }) => alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.1rem;
      border-color: ${({ theme }) => theme.palette.primary.main};
    }
    flex: 1 1 auto;
    & .MuiInputBase-input {
      margin: 0;
      padding: 0;
    }
    & .MuiOutlinedInput-notchedOutline {
      border-color: transparent;
      border: 0;
    }
  }
`;

const StyledDateField = styled(DateField)`
  && {
    & .MuiInputBase-root {
      background-color: ${({ theme }) => (theme.palette.backdrop.main)};
      border-radius: ${({ theme }) => theme.shape.borderRadius}px;
      padding: ${({ theme }) => theme.spacing(1, 2)};
      transition: ${({ theme }) => theme.transitions.create(['border-color', 'box-shadow'])};
      border: 1px solid;
      border-color: transparent;
      height: 40px;
      box-shadow: ${({ theme }) => alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.1rem;
      border-color: ${({ theme }) => theme.palette.primary.main};
    }
    flex: 1 1 auto;
    & .MuiInputBase-input {
      margin: 0;
      padding: 0;
    }
    & .MuiOutlinedInput-notchedOutline {
      border-color: transparent;
      border: 0;
    }
  }
`;

const ArrayFieldContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
`;

const EditableArrayContainer = styled.ul`
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  flex: 1 1 auto;
  flex-direction: row;
  background-color: ${({ theme }) => (theme.palette.backdrop.main)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  margin: 0;
  padding: 0;
  transition: ${({ theme }) => theme.transitions.create(['border-color', 'box-shadow'])};
  border: 1px solid;
  border-color: transparent;
  box-shadow: ${({ theme }) => alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.1rem;
  border-color: ${({ theme }) => theme.palette.primary.main};
  padding-bottom: ${({ theme }) => theme.spacing(1)};
  padding-right: ${({ theme }) => theme.spacing(1)};
  cursor: text;
`;

const FixedArrayContainer = styled.ul`
  display: flex;
  list-style: none;
  flex: 1 1 auto;
  flex-direction: row;
  margin: 0;
  padding: 0;
`;

const ArrayItem = styled(ListItem)`
  && {
    width: fit-content;
    height: fit-content;
    padding: ${({ theme }) => `${theme.spacing(1)} 0 0 ${theme.spacing(1)}`};
  }
`;

const ArrayInputItem = styled(ListItem)`
  && {
    width: fit-content;
    height: fit-content;
    flex: 1 1 auto;
    padding: ${({ theme }) => `${theme.spacing(1)} 0 0 ${theme.spacing(1)}`};
  }
`;

const ArrayChip = styled(Chip)`
  && {
    height: 22px;
  }
`;

const FixedArrayItem = styled(ListItem)`
  && {
    width: fit-content;
    padding: ${({ theme }) => `0 ${theme.spacing(1)} 0 0`};
  }
`;

const ArrayInput = styled(InputBase)`
  && {
    background: transparent;
    border: none;
    width: 100%;
    height: 22px;
    padding: 0;
    & .MuiInputBase-input {
      height: 22px;
      padding: 0;
      width: 100%;
    }
  }
`;

export { RootContainer,
  Dropdown,
  ArrayMenu,
  DropdownItem,
  ArrayDropdownItem,
  ArrayItem,
  ArrayInputItem,
  ArrayChip,
  ArrayInput,
  EditFieldContainer,
  FixedArrayItem,
  StyledTextField,
  Display,
  StyledDateField,
  StyledDateTimeField,
  FieldButton,
  ArrayFieldContainer,
  EditableArrayContainer,
  FixedArrayContainer,
  StyledAutocompleteField,
  DropdownIcon,
  DisplayContainer };
